import { useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Image, Menu } from "semantic-ui-react";
import Swal from "sweetalert2";
import jwtDecode from "jwt-decode";
import useLatestVersionAlert from '../components/Common/SharedComponents/useLatestVersionAlert';
import AxiosInstance, { DecodedToken, checkTimeIntervalOfRefreshTokenExpiration, setTokenRefreshedCallback, lastServerTime } from '../helpers/axiosInstance';
import { clearIDB } from '../helpers/BlobImages';
import imageLogo from "../assets/images/logo.svg";
import icoCases from "../assets/images/ico-cases.svg";
import icoUser from "../assets/images/ico-user.svg";
import icoReports from "../assets/images/ico-reports.svg";
import icoSettings from "../assets/images/ico-settings.svg";
import icoLogout from "../assets/images/logout.svg";
import AppContext, { token_config } from '../AppContext';

let callUnsaveAlert: any = null;
export function setCallUnsaveAlert(callback: () => void) {
  // Set the callback function
  callUnsaveAlert = callback;
}

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthTokens, userInfo } = useContext(AppContext);
  const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];

  const { checkVersionUpdate } = useLatestVersionAlert();

  useEffect(() => {
    // check for the new version of manifest.json so the build is new and prompt for the hard reload of the application.
    checkVersionUpdate();
  }, [location.key]);

  const removeLocalstorage = async () => {
    localStorage.removeItem(token_config.ACCESS_TOKEN);
    localStorage.removeItem(token_config.REFRESH_TOKEN);
    setAuthTokens(null);
    clearTimeout(initialExpireTimeout);
    clearTimeout(minuteExpireTimeout);
    await clearIDB();
    window.location.href = '/login';
  }

  const handleSignOut = async () => {
    try {
      localStorage.removeItem('dashboardFilter');
      await AxiosInstance.get('/Auth/SignOut');
    } catch (error) {
      console.error(error);
    } finally {
      removeLocalstorage();
    }
  }

  let sessionExpirationWarningShown = false; // Add this flag
  let initialExpireTimeout: any = null;
  let minuteExpireTimeout: any = null;

  // Function to display the Swal.fire dialog with countdown
  const showAlertSessionExpirationWarning = (minutesRemaining: number) => {
    // Update the countdown
    if (minutesRemaining > 0) {
      Swal.fire({
        icon: "warning",
        text: `Your session will end in ${minutesRemaining} minutes due to inactivity. If you are still working, click OK to extend the session.`,
        confirmButtonText: "OK",
      }).then((result) => {
        sessionExpirationWarningShown = false;
        if (result.isConfirmed) {
          const refreshToken = localStorage.getItem(token_config.REFRESH_TOKEN);
          const accessToken = localStorage.getItem(token_config.ACCESS_TOKEN);
          AxiosInstance
            .post("/Auth/refresh", {
              expiredAccessToken: accessToken,
              refreshToken: refreshToken,
            })
            .then(({ status, data }) => {
              if (status && status === 200) {
                localStorage.setItem(token_config.ACCESS_TOKEN, data.accessToken);
                localStorage.setItem(token_config.REFRESH_TOKEN, data.refreshToken);
                window.location.reload();
              } else {
                removeLocalstorage();
              }
            });
        }
      });

      minuteExpireTimeout = setTimeout(function () {
        showAlertSessionExpirationWarning(minutesRemaining - 1);
      }, 60000); // 1 minute (60000 milliseconds)
    } else {
      removeLocalstorage();
    }
  }

  // Function to show session expiration warning
  const showSessionExpirationWarning = () => {
    if (!sessionExpirationWarningShown) {
      showAlertSessionExpirationWarning(checkTimeIntervalOfRefreshTokenExpiration / 60);
      sessionExpirationWarningShown = true;
    }
  };

  // Add a timer to show the warning 10 minutes before token expiration
  const startSessionExpirationTimer = (tokenExpirationTime: any) => {
    const timeUntilExpiration = tokenExpirationTime - lastServerTime;

    if (timeUntilExpiration > checkTimeIntervalOfRefreshTokenExpiration) {
      // Set a timer to show the warning
      initialExpireTimeout = setTimeout(() => {
        showSessionExpirationWarning();
      }, (timeUntilExpiration - checkTimeIntervalOfRefreshTokenExpiration) * 1000);
    }
  };

  useEffect(() => {
    function setRefreshTimer() {
      const refreshToken = localStorage.getItem(token_config.REFRESH_TOKEN);
      if (refreshToken) {
        const tokenData = jwtDecode<DecodedToken>(refreshToken);
        if (tokenData.exp) {
          clearTimeout(initialExpireTimeout);
          clearTimeout(minuteExpireTimeout);
          startSessionExpirationTimer(tokenData.exp);
        }
      }
    }
    setTokenRefreshedCallback(setRefreshTimer);
    setRefreshTimer();
  }, [location.key]);

  const redirectUrl = async (e: any, url: string, isReload = false) => {
    e.preventDefault();
    localStorage.removeItem('dashboardFilter');
    let returnResponse = true;
    if (typeof callUnsaveAlert === 'function') {
      returnResponse = await callUnsaveAlert();
    }
    if (returnResponse) {
      callUnsaveAlert = null;
      if (isReload) {
        window.location.href = url;
      } else {
        navigate(url);
      }
    }
  }

  return (
    <header className="header-main">
      <div className="header__content">
        <div style={{ cursor: 'pointer' }} className="logo" onClick={(e) => {
          localStorage.setItem('caseDataGridTypeId', '1');
          redirectUrl(e, '/dashboard', true);
        }}>
          <Image src={imageLogo} />
        </div>
        <div className="nav">
          <Menu icon vertical={true}>
            {privileges.includes(token_config.MANAGE_CASES) && (
              <Menu.Item active={location.pathname === '/dashboard' || location.pathname.indexOf('/case') !== -1} className="header-menu-bg" name="gamepad">
                <a href="#" onClick={(e) => redirectUrl(e, '/dashboard')}>
                  <Image src={icoCases} alt="Cases" title="Cases" />
                </a>
              </Menu.Item>
            )}
            {privileges.includes(token_config.MANAGE_CONSTITUENTS) && (
              <Menu.Item active={location.pathname === '/constituents' || location.pathname.indexOf('/constituent') !== -1} className="header-menu-bg" name="gamepad">
                <a href="#" onClick={(e) => redirectUrl(e, '/constituents')}>
                  <Image src={icoUser} alt="Constituents" title="Constituents" />
                </a>
              </Menu.Item>
            )}
            {privileges.includes(token_config.VIEW_REPORTS) && (
              <Menu.Item active={location.pathname === '/reports'} className="header-menu-bg" name="gamepad">
                <a href="#" onClick={(e) => redirectUrl(e, '/reports')}>
                  <Image src={icoReports} alt="Reports" title="Reports" />
                </a>
              </Menu.Item>
            )}
          </Menu>
        </div>
      </div>
      <div className="header__entry">
        <a href="#" onClick={(e) => redirectUrl(e, privileges.includes(token_config.MANAGE_USERS) ? '/users' : '/contact-form')} className="header-menu-bg" style={{
          marginLeft: '15px'
        }}>
          <Image src={icoSettings} alt="Settings" title="Settings" />
        </a>
        <a className='header-menu-bg' onClick={(e) => { e.stopPropagation(); handleSignOut() }} style={{
          marginLeft: '15px',
          marginTop: '3rem'
        }} title="Sign out">
          <Image src={icoLogout} alt="Sign out" title="Sign out" style={{ marginLeft: '0.2rem' }} />
        </a>
      </div>
    </header>
  );
};

export default Header;
